import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Languages, LogOut, Menu, LayoutDashboard, Shield, LogIn, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { resolveAssetUrl } from "@/lib/assets";

export const Header = () => {
  const { t, lang, toggle } = useLanguage();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [branding, setBranding] = useState({ logo_url: null, app_name: "Zabatna" });
  const [logoBroken, setLogoBroken] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    api.get("/public/branding").then((r) => setBranding(r.data)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    setDrawerOpen(false);
    await logout();
    navigate("/");
  };

  const closeDrawer = () => setDrawerOpen(false);

  // Mobile drawer side: open from the leading edge (right in RTL, left in LTR).
  const drawerSide = lang === "ar" ? "right" : "left";

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/85 backdrop-blur supports-[backdrop-filter]:bg-white/65"
      data-testid="app-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 min-w-0 group" data-testid="header-logo-link">
          {/* Logo mark — uses admin-uploaded image when valid, otherwise a
              styled letter mark. The `onError` swap protects us from broken
              image URLs (file deleted, upload corrupted, etc.). */}
          {branding.logo_url && !logoBroken ? (
            <img
              src={resolveAssetUrl(branding.logo_url)}
              alt={branding.app_name}
              onError={() => setLogoBroken(true)}
              className="h-9 w-9 object-contain shrink-0"
              data-testid="header-logo-img"
            />
          ) : (
            <div
              className="h-9 w-9 bg-gradient-to-br from-slate-900 to-slate-700 text-white grid place-items-center rounded-lg font-heading font-extrabold text-lg shadow-sm ring-1 ring-slate-900/5 shrink-0 group-hover:from-slate-800 group-hover:to-slate-600 transition-colors"
              data-testid="header-logo-fallback"
            >
              ز
            </div>
          )}
          <span
            className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 leading-none truncate"
            data-testid="header-brand-text"
          >
            {lang === "ar" ? "زبطنا" : "Zabatna"}
          </span>
        </Link>

        {/* ───── Desktop nav (≥ md) ───── */}
        <nav className="hidden md:flex items-center gap-2" data-testid="header-nav-desktop">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={toggle}
            data-testid="lang-toggle-btn"
            className="gap-1.5"
          >
            <Languages className="w-4 h-4" />
            {t("lang_toggle")}
          </Button>

          {user ? (
            <>
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" data-testid="nav-dashboard-btn">
                  {t("nav_dashboard")}
                </Button>
              </Link>
              {user.role === "admin" && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" data-testid="nav-admin-btn">
                    {t("nav_admin")}
                  </Button>
                </Link>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="gap-1.5 px-4 transition-colors duration-200 hover:bg-slate-50 hover:border-slate-400"
              >
                <LogOut className="w-4 h-4" />
                {t("nav_logout")}
              </Button>
            </>
          ) : (
            <>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="nav-login-btn">
                  {t("cta_login")}
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="nav-register-btn" className="bg-slate-900 hover:bg-slate-800">
                  {t("cta_register")}
                </Button>
              </Link>
            </>
          )}
        </nav>

        {/* ───── Mobile nav (< md): language toggle + hamburger drawer ───── */}
        <div className="flex md:hidden items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggle}
            data-testid="lang-toggle-mobile-btn"
            aria-label={t("lang_toggle")}
            className="h-11 w-11"
          >
            <Languages className="w-5 h-5" />
          </Button>
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={lang === "ar" ? "فتح القائمة" : "Open menu"}
                data-testid="header-mobile-menu-btn"
                className="h-11 w-11"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side={drawerSide}
              className="w-[82vw] max-w-xs p-0 flex flex-col"
              data-testid="header-mobile-drawer"
            >
              <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-200">
                <SheetTitle className="text-start font-heading text-lg">
                  {lang === "ar" ? "القائمة" : "Menu"}
                </SheetTitle>
              </SheetHeader>
              <nav className="flex-1 overflow-y-auto px-3 py-3 flex flex-col gap-1">
                {user ? (
                  <>
                    <div className="px-3 py-2 mb-1 text-xs text-slate-500 truncate text-start">
                      {user.email}
                    </div>
                    <Link to="/dashboard" onClick={closeDrawer}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start gap-3 h-12 text-base"
                        data-testid="mobile-nav-dashboard-btn"
                      >
                        <LayoutDashboard className="w-5 h-5" />
                        {t("nav_dashboard")}
                      </Button>
                    </Link>
                    {user.role === "admin" && (
                      <Link to="/admin" onClick={closeDrawer}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start gap-3 h-12 text-base"
                          data-testid="mobile-nav-admin-btn"
                        >
                          <Shield className="w-5 h-5" />
                          {t("nav_admin")}
                        </Button>
                      </Link>
                    )}
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={closeDrawer}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start gap-3 h-12 text-base"
                        data-testid="mobile-nav-login-btn"
                      >
                        <LogIn className="w-5 h-5" />
                        {t("cta_login")}
                      </Button>
                    </Link>
                    <Link to="/register" onClick={closeDrawer}>
                      <Button
                        className="w-full justify-start gap-3 h-12 text-base bg-slate-900 hover:bg-slate-800 mt-2"
                        data-testid="mobile-nav-register-btn"
                      >
                        <UserPlus className="w-5 h-5" />
                        {t("cta_register")}
                      </Button>
                    </Link>
                  </>
                )}
              </nav>
              {user && (
                <div className="border-t border-slate-200 p-3">
                  <Button
                    variant="outline"
                    onClick={handleLogout}
                    data-testid="mobile-logout-btn"
                    className="w-full justify-start gap-3 h-12 text-base"
                  >
                    <LogOut className="w-5 h-5" />
                    {t("nav_logout")}
                  </Button>
                </div>
              )}
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
